/**
 * ReviewHelpGuide - user-friendly guide explaining the review feature
 *
 * Rendered inside GlobalModal when the user clicks the "?" icon in
 * the review session header. The content is non-technical and written
 * for end-users (lawyers, compliance, etc.).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiFileMagnifyingGlassLight,
  PiUploadSimpleLight,
  PiSpinnerGapLight,
  PiCheckCircleLight,
  PiXCircleLight,
  PiPencilSimpleLight,
  PiArrowUpRightLight,
  PiWarningCircleLight,
  PiSealCheckLight,
  PiDownloadSimpleLight,
  PiUsersLight,
  PiClockCounterClockwiseLight,
  PiArrowCounterClockwiseLight,
  PiCaretDownLight,
  PiCaretUpLight,
  PiShieldCheckLight,
  PiFileTextLight,
  PiLightbulbLight,
  PiArrowRightLight,
} from 'react-icons/pi';

interface SectionProps {
  icon: React.ReactElement;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Section: React.FC<SectionProps> = ({ icon, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 text-primary-500">{icon}</span>
          <span className="text-sm text-gray-900 dark:text-gray-100">{title}</span>
        </div>
        {open ? (
          <PiCaretUpLight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <PiCaretDownLight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 text-sm text-gray-600 dark:text-gray-300 leading-relaxed space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

interface StepProps {
  number: number;
  title: string;
  description: string;
}

const Step: React.FC<StepProps> = ({ number, title, description }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
      <span className="text-xs text-primary-600 dark:text-primary-400">{number}</span>
    </div>
    <div>
      <p className="font-medium text-gray-800 dark:text-gray-200">{title}</p>
      <p className="text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
    </div>
  </div>
);

const ReviewHelpGuide: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="px-6 pb-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar-main">
      {/* Intro */}
      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
        <div className="flex gap-3">
          <PiFileMagnifyingGlassLight className="w-6 h-6 text-primary-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-primary-700 dark:text-primary-300">
              {t('What is Document Review?')}
            </p>
            <p className="text-sm text-primary-600 dark:text-primary-400 mt-1 leading-relaxed">
              {t('Document Review uses AI to analyze your legal documents - contracts, agreements, policies - and identifies potential issues, risks, and areas that need attention. You then review each finding, decide what to do, and once your team agrees, finalize the review.')}
            </p>
          </div>
        </div>
      </div>

      {/* How it works - step by step */}
      <Section
        icon={<PiArrowRightLight className="w-5 h-5" />}
        title={t('How It Works - Step by Step')}
        defaultOpen={true}
      >
        <div className="space-y-4">
          <Step
            number={1}
            title={t('Upload Documents')}
            description={t('Select one or more documents to review. You can upload contracts, agreements, or any legal document. Choose a playbook (a set of review rules) that matches what you need.')}
          />
          <Step
            number={2}
            title={t('AI Analysis')}
            description={t('The AI reads through your documents and flags findings - specific issues, risks, or suggestions. This usually takes a few moments depending on the document length.')}
          />
          <Step
            number={3}
            title={t('Review Findings')}
            description={t('Go through each finding one by one. Read the issue, see the original text, and check the AI\'s suggestion. Then make your decision on each.')}
          />
          <Step
            number={4}
            title={t('Sign Off & Finalize')}
            description={t('Once all findings have been decided, your team can sign off. When everyone has signed off, the review is finalized and you can download the final documents.')}
          />
        </div>
      </Section>

      {/* Understanding Findings */}
      <Section
        icon={<PiLightbulbLight className="w-5 h-5" />}
        title={t('Understanding Findings')}
      >
        <p>{t('Each finding represents something the AI identified in your document that may need attention. Here\'s what you\'ll see:')}</p>

        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1 w-2 h-2 rounded-full bg-red-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Critical')}</span>
              <span className="text-gray-500 dark:text-gray-400"> - {t('Serious issues that could pose significant legal risk. These should be addressed urgently.')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1 w-2 h-2 rounded-full bg-orange-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Major')}</span>
              <span className="text-gray-500 dark:text-gray-400"> - {t('Important issues that should be reviewed carefully and likely need changes.')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1 w-2 h-2 rounded-full bg-yellow-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Minor')}</span>
              <span className="text-gray-500 dark:text-gray-400"> - {t('Smaller issues or improvements. Good to fix, but not urgent.')}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1 w-2 h-2 rounded-full bg-blue-500" />
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Info')}</span>
              <span className="text-gray-500 dark:text-gray-400"> - {t('Informational notes or observations. No action required, but good to be aware of.')}</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Making Decisions */}
      <Section
        icon={<PiCheckCircleLight className="w-5 h-5" />}
        title={t('Making Decisions on Findings')}
      >
        <p>{t('For each finding, you have four options:')}</p>

        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <PiCheckCircleLight className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Accept')}</span>
              <p className="text-gray-500 dark:text-gray-400 mt-0.5">{t('You agree with the AI\'s suggestion. The recommended change will be applied to the final document.')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <PiXCircleLight className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Reject')}</span>
              <p className="text-gray-500 dark:text-gray-400 mt-0.5">{t('You disagree with the suggestion. The original text stays as-is and no change is made.')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <PiPencilSimpleLight className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Modify')}</span>
              <p className="text-gray-500 dark:text-gray-400 mt-0.5">{t('You want to make a change, but with your own wording instead of the AI\'s suggestion. Write your own text and it will be used in the final document.')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <PiArrowUpRightLight className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <span className="font-medium text-gray-800 dark:text-gray-200">{t('Escalate')}</span>
              <p className="text-gray-500 dark:text-gray-400 mt-0.5">{t('Flag this finding for further discussion or senior review. Use this when you\'re unsure or the issue needs wider input.')}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-start gap-2">
            <PiArrowCounterClockwiseLight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium">{t('Changed your mind?')}</span> {t('You can undo any decision and go back to the original state. Just click "Undo" on the finding.')}
            </p>
          </div>
        </div>
      </Section>

      {/* Risk Score */}
      <Section
        icon={<PiShieldCheckLight className="w-5 h-5" />}
        title={t('Risk Score')}
      >
        <p>{t('The risk score is a quick indicator of the overall risk level found in your documents. It\'s calculated based on the number and severity of findings:')}</p>
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-500" />
            <span>{t('Low (0–3) - Few or minor issues. The document is in good shape.')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-yellow-500" />
            <span>{t('Medium (4–6) - Some issues that should be reviewed.')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-500" />
            <span>{t('High (7–8) - Significant issues requiring attention.')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500" />
            <span>{t('Critical (9–10) - Serious concerns that need immediate review.')}</span>
          </div>
        </div>
      </Section>

      {/* Document Viewer */}
      <Section
        icon={<PiFileTextLight className="w-5 h-5" />}
        title={t('Viewing Documents')}
      >
        <p>{t('Click on a document name or "View Document" to open the document viewer. This lets you:')}</p>
        <ul className="mt-2 space-y-1.5 list-disc list-inside">
          <li>{t('See the full document with findings highlighted in context')}</li>
          <li>{t('Click on any highlighted area to jump to that finding')}</li>
          <li>{t('Toggle between the original text and the AI\'s suggested changes')}</li>
        </ul>
      </Section>

      {/* Collaboration */}
      <Section
        icon={<PiUsersLight className="w-5 h-5" />}
        title={t('Collaboration')}
      >
        <p>{t('Reviews are collaborative. You can work together with your team in real time:')}</p>
        <ul className="mt-2 space-y-1.5 list-disc list-inside">
          <li>{t('Invite colleagues to the review and assign them view or edit access')}</li>
          <li>{t('See each other\'s decisions and notes as they happen')}</li>
          <li>{t('Add notes to any finding to explain your reasoning')}</li>
          <li>{t('Track all changes in the Activity panel on the right side')}</li>
        </ul>
      </Section>

      {/* Sign-Off & Finalization */}
      <Section
        icon={<PiSealCheckLight className="w-5 h-5" />}
        title={t('Sign-Off & Finalization')}
      >
        <p>{t('Once every finding has been decided, the review moves to the sign-off stage:')}</p>
        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-amber-500" />
            <span>{t('All collaborators with edit or owner access need to sign off to confirm they agree with the decisions.')}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span>{t('Once everyone has signed off, the review is finalized. No more changes can be made unless an owner reopens it.')}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-blue-500" />
            <span>{t('After finalization, you can download the final documents - either a clean version with all accepted changes applied, or a redline version showing what was changed.')}</span>
          </div>
        </div>
      </Section>

      {/* Activity Log */}
      <Section
        icon={<PiClockCounterClockwiseLight className="w-5 h-5" />}
        title={t('Activity Log')}
      >
        <p>{t('The Activity panel (toggle it from the bar at the top) shows a complete timeline of everything that happened during the review:')}</p>
        <ul className="mt-2 space-y-1.5 list-disc list-inside">
          <li>{t('When findings were created by the AI')}</li>
          <li>{t('Who made each decision and when')}</li>
          <li>{t('Sign-off events')}</li>
          <li>{t('Any changes to the review status')}</li>
        </ul>
        <p className="mt-2">{t('Click on any finding reference in the log to jump directly to that finding.')}</p>
      </Section>

      {/* Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-2">
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">💡 {t('Tips')}</p>
        <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1.5 list-disc list-inside">
          <li>{t('Use the severity and decision filters to focus on what matters most.')}</li>
          <li>{t('Start with Critical findings - they\'re the highest priority.')}</li>
          <li>{t('Add notes to your decisions so your team understands your reasoning.')}</li>
          <li>{t('The review auto-advances to the next undecided finding after each decision.')}</li>
        </ul>
      </div>
    </div>
  );
};

export default ReviewHelpGuide;
